const TOKEN_KEY = 'jwt_token'
const REFRESH_TOKEN_KEY = 'jwt_refresh_token'

/**
 * Treats the literal strings `"null"`/`"undefined"` (and blanks) as "no token".
 * `localStorage.setItem(key, undefined)` stores the *string* "undefined", which is
 * truthy and would otherwise be sent as `Bearer undefined` — the backend JWT parser
 * rejects that with "Found: 0 periods".
 */
function normalizeStoredToken(raw: string | null): string | null {
  if (!raw || raw === 'null' || raw === 'undefined') return null
  return raw
}

export const authService = {
  getToken(): string | null {
    return normalizeStoredToken(localStorage.getItem(TOKEN_KEY))
  },

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  },

  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  getRefreshToken(): string | null {
    return normalizeStoredToken(localStorage.getItem(REFRESH_TOKEN_KEY))
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token)
  },

  hasToken(): boolean {
    return !!this.getToken()
  },

  /**
   * Returns the `Bearer <token>` Authorization header value, or `null` when there
   * is no token. Always prefer this over interpolating `getToken()` directly into
   * a header — `Bearer null` is a frequent footgun that causes the backend JWT
   * parser to log "Found: 0 periods" errors for empty/null tokens.
   */
  getAuthorizationHeader(): string | null {
    const token = this.getToken()
    return token ? `Bearer ${token}` : null
  },

  /**
   * Authorization header for `/api/auth/refresh`. Prefers the refresh token but
   * falls back to the access token (the backend refresh endpoint accepts either),
   * and returns `null` — never `Bearer null` — when neither is present. Always use
   * this instead of interpolating the token directly into the header.
   */
  getRefreshAuthorizationHeader(): string | null {
    const token = this.getRefreshToken() ?? this.getToken()
    return token ? `Bearer ${token}` : null
  },

  decodeToken(): any | null {
    const token = this.getToken()
    if (!token) return null

    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (e) {
      return null
    }
  },

  isTokenExpired(): boolean {
    const decoded = this.decodeToken()
    if (!decoded?.exp) return true
    return Date.now() >= decoded.exp * 1000
  },

  isTokenExpiringSoon(minutes: number = 30): boolean {
    const decoded = this.decodeToken()
    if (!decoded?.exp) return true
    const expTime = decoded.exp * 1000
    const warningTime = minutes * 60 * 1000
    return Date.now() >= expTime - warningTime
  }
}